import React, { useCallback, useEffect, useState} from "react"
import { Form, Button, Alert } from 'react-bootstrap'
import Skeleton from 'react-loading-skeleton'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import { useForm } from 'react-hook-form'
import { navigate } from '@reach/router'
import moment from 'moment'

import './PeriodForm.css'
import PeriodNameErrors from "./PeriodNameErrors"
import PeriodDateRange from "./PeriodDateRange"
import PeriodsApi from "../../api/PeriodsApi"
import { ApiException, ValidationException } from "../../api/exceptions"

const PeriodForm = ({ periodData, isLoading }) => {
  const [startDate, setStartDate] = useState(null );
  const [endDate, setEndDate] = useState(null );

  const recordExists = useCallback(() => (!!periodData?.slug), [periodData?.slug]);

  /** return serialized data object to set up as default when is update form */
  const serializeData = () => {
    if( periodData && Object.keys(periodData) != false ){
      const { nombre, abreviatura, inicio_periodo, fin_periodo, activo, abierto_para_inscripciones } = periodData
      let [year, half] = nombre.split('-')

      return {
        year,
        half,
        abreviatura,
        inicio_periodo: !!inicio_periodo ? moment(inicio_periodo) : undefined,
        fin_periodo: !!fin_periodo ? moment(fin_periodo) : undefined,
        activo,
        abierto_para_inscripciones
      }
    } else {

      return {
        year: moment().year(),
        half: Math.round(moment().quarter() / 2)
      }
    }
  }
  const { register, handleSubmit, formState: { errors }, control, setError, setValue, clearErrors, reset } = useForm()

  const alert = withReactContent(Swal)
  
  const onSubmit = data => {
    data.nombre = `${data.year}-${data.half}`;

    if (data.inicio_periodo) {
      data.inicio_periodo = moment(data.inicio_periodo).format('yyyy-MM-DD')
    }

    if (data.fin_periodo) {
      data.fin_periodo = moment(data.fin_periodo).format('yyyy-MM-DD')
    }

    const api = new PeriodsApi();
    
    (recordExists() ? api.update(periodData.slug, data) : api.store(data))
      .then( () => {
        alert.fire({
            title: 'Exito',
            text: 'El periodo se dio de alta correctamente',
            icon: 'success'
          })

          navigate('/admin/periodos');
        })
      .catch( error => {
        let message = 'Hubo un error al procesar la solicitud. Intente más tarde.';

        if (error instanceof ValidationException) {
          message = Object.values(error.errors);
        } else if (error instanceof ApiException) {
          message = error.message;
        }

        alert.fire({
          title: 'Error',
          text: message,
          icon: 'error'
        })
      })
  }

  useEffect( () => {
    window.scrollTo(0, 0)
    /** set data when is update form */
    const data = serializeData();
    reset(data)

    if (data.inicio_periodo) {
      setStartDate(new Date(data.inicio_periodo))
    }

    if (data.fin_periodo) {
      setEndDate(new Date(data.fin_periodo))
    }

  }, [periodData])

  /**
   * Register date range form fields.
   */
  useEffect(() => {
    register('inicio_periodo', { required: false })
    register('fin_periodo', { required: false })
  }, [])

  useEffect(() => {
    setValue('inicio_periodo', startDate);
  }, [startDate])

  useEffect(() => {
    setValue('fin_periodo', endDate);
  }, [endDate])

  if (isLoading) {
    return (
      <>
        <Skeleton height={600} />
        <Skeleton height={80} />
      </>
    )
  }

  return (
        <Form onSubmit={handleSubmit(onSubmit)} >
          <Form.Row className="periodNameRow justify-content-center">
            <Form.Group>
                <Form.Label htmlFor="year">Año</Form.Label>
                <Form.Control
                    id="year"
                    type="number"
                    {...register('year', {
                      required: 'Año requerido.',
                      pattern: {
                        value: /\d{4}/,
                        message: 'Año inválido.'
                      }
                    })}
                />
            </Form.Group>
            <Form.Group>
              <span>‒</span>
            </Form.Group>
            <Form.Group>
                <Form.Label htmlFor="half">Mitad</Form.Label>
                <Form.Control 
                  as="select"
                  id="half"
                  className="custom-select"
                  {...register('half', {
                    required: 'Mitad es requerida.'
                  })}>
                    <option value="1">1</option>
                    <option value="2">2</option>
                </Form.Control>
            </Form.Group>
          </Form.Row>
          <PeriodNameErrors
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            nameError={errors.name}
            yearError={errors.year}
            halfError={errors.half}
          />
          {/* <Form.Group>
              <Form.Label>Abreviatura</Form.Label>
              <Form.Control
                  id="abreviatura"
                  type="text"
                  {...register('abreviatura', {
                    required: false
                  })}
              />
              {errors.abreviatura && <Alert variant='danger' className='mt-2'>{errors.abreviatura.message}</Alert>}
          </Form.Group> */}
          <PeriodDateRange
            start={startDate} setStart={setStartDate}
            end={endDate} setEnd={setEndDate}        
            startError={errors.inicio_periodo?.message}
            endError={errors.fin_periodo?.message}
          />
          <Form.Group className="mt-5">
            <Form.Check 
              type="switch"
              id="activo"
              label="En curso"
              className="custom-switch-md"
              {...register('activo')}
            />
          </Form.Group>
          <Form.Group>
              <Form.Check 
                type="switch"
                id="abierto_para_inscripciones"
                label="Abierto para Inscripciones"
                className="custom-switch-md"
                {...register('abierto_para_inscripciones')}
              />
          </Form.Group>
          <Button type='submit' variant="primary" size="lg" block>
              Guardar
          </Button>
      </Form>        
  )
}

export default PeriodForm
